import { Box, Grid, Paper, Typography } from '@mui/material'
import React, { Fragment, useEffect, useState } from 'react'
import { Status } from '../../hooks/useFetchState'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import { AsnReturnOrderDetailReturnBatches, createAsn } from './api'
import { Filter } from '../_WMS/FilterComponent'
import { usePagination } from '../../hooks/usePagination'
import {
  AsnCreateDto,
  AsnDetailsCreateDto,
  OrderDetailReturnReasonCreateDto,
  OrderDetailReturnReasonsBatchInfoDto,
  OrderDetailReturnReasonsCreateDto,
  RelatedBatch
} from './types'
import { Modal } from '../_EOS/Modal'
import { useModal } from '../../hooks/useModal'
import { SimpleBatchSelector } from './components/SimpleBatchSelector'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { AsnReturnDto } from './SearchReturn'
import { eAsnState } from '../../enums/eAsnState'
import { eTypeAsnDocument } from '../../enums/eTypeAsnDocument'
import { eTypeAsnSource } from '../../enums/eTypeAsnSource'
import { eTypeAsnProcess } from '../../enums/eTypeAsnProcess'
import { useSuppliers } from '../../hooks'
import { useSuppliersOrigin } from '../../hooks/useSuppliersOrigin'
import { eTypeAsnIncome } from '../../enums/eTypeAsnIncome'
import { LoadingButton } from '../_EOS/Button/LoadingButton'
import { GenericTableV2, TableColumn } from '../_EOS/GenericTable'
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { formatExpirationDate } from '../../utils/dateHelper'
import { SuppliersDDL } from '../Supplier/SuppliersDDL'
import { Loading } from '../UI/Loading/Loading'
import { SupplierOriginDDL } from '../SupplierOrigin/SupplierOriginDDL'

export interface ReturnBatchSelecionProps {
  selectedOrderId: number
  selectedTypeAsnIncomeId: number
  handleStep: React.Dispatch<React.SetStateAction<number>>
  handleSnackbar: (msg: string | undefined, status: boolean) => void
  newPreInbound: boolean
  selectedAsnReturnDto: AsnReturnDto | undefined
  userId: number
  setSelectedAsnId: React.Dispatch<React.SetStateAction<number>>
  setSelectedAsnNumber: React.Dispatch<React.SetStateAction<string>>
  setselectedAsnStateId: React.Dispatch<React.SetStateAction<number>>
  selectedWarehouseId: number
  selectedPreInboundId: number
  selectedTrackingNumber: string
  selectedCourier: string
}

// Interfaz para los lotes seleccionados
interface SelectedBatchInfo {
  rowId: string // Nuevo: identificador único de fila
  orderDetailId: number
  batchId: number
  batchNumber: string
  expirationDate?: string
}

// 2. Crea una función helper para generar el rowId único
const generateRowId = (
  row: OrderDetailReturnReasonsBatchInfoDto,
  index: number
): string => {
  // Combina orderDetailId con índice y otros campos únicos si están disponibles
  return `${row.orderDetailId}_${index}_${row.returnReasonId || ''}`
}

export const generateTableHeaders = (items: SelectedBatchInfo[]) => {
  const tableHeader: TableColumn<OrderDetailReturnReasonsBatchInfoDto>[] = [
    {
      key: 'sku',
      title: 'SKU',
      sortable: false
    },
    {
      key: 'quantity',
      title: 'Cantidad',
      sortable: false
    },
    {
      key: 'returnReason',
      title: 'Motivo',
      sortable: false
    },
    {
      key: 'orderDetailId',
      title: 'Fecha/Lote Seleccionado',
      headerTooltip:
        'El sistema selecciona automáticamente el lote cuando hay solo uno disponible en el producto. Si existen varios lotes, el operador debe elegir manualmente el correcto antes de continuar.',
      sortable: false,
      render: (
        _: unknown,
        row: OrderDetailReturnReasonsBatchInfoDto,
        index: number
      ) => {
        // Genera el rowId igual que en la tabla
        const rowId = generateRowId(
          row as OrderDetailReturnReasonsBatchInfoDto,
          index || 0
        )

        const result = items?.find((el) => el.rowId === rowId)

        if (!result) return ''

        return (
          result.batchNumber ||
          formatExpirationDate(result.expirationDate, 'dd-MM-yyyy')
        )
      }
    }
  ]

  return tableHeader
}

// Selección de Información y Creación Asn
export const ReturnBatchSelecion: React.FC<ReturnBatchSelecionProps> = ({
  handleStep,
  handleSnackbar,
  selectedOrderId,
  selectedAsnReturnDto,
  userId,
  setSelectedAsnId,
  setSelectedAsnNumber,
  setselectedAsnStateId,
  selectedWarehouseId,
  selectedTypeAsnIncomeId,
  selectedPreInboundId,
  selectedTrackingNumber,
  selectedCourier
}) => {
  const productDunModal = useModal<{
    relatedBatches: RelatedBatch[]
    row: OrderDetailReturnReasonsBatchInfoDto
    rowId: string
  }>('')
  // aallende
  const [selectedBatches, setSelectedBatches] = useState<SelectedBatchInfo[]>(
    []
  )

  // determina si se debe mostrar la grilla para selecicción de lotes. si existe algun sku con fefo la muestra. Ya que puede venir una devolución con sku con fefo y otro sku sin fefo.
  const [anyFefoEnabled, setAnyFefoEnabled] = useState<Boolean | undefined>(
    undefined
  )

  const { state: stateOrderDetailReturnReasonsBatch, callApi } =
    useApi<ApiResponse<OrderDetailReturnReasonsBatchInfoDto>>()
  const pagination = usePagination(5)
  const [selectedFilters] = useState<Filter[]>([])
  const [selectedSupplier, setSupplier] = useState(0)
  const [selectedSupplierOrigin, setSupplierOrigin] = useState(0)
  const { state: stateCreateASN, callApi: CreateasncallApi } = useApi()
  const [loading, setLoading] = useState(false)
  const [supplierError, setSupplierError] = useState('')

  // State para creación de Asn automatica
  const [autoSelectedSupplierOriginOk, setAutoSelectedSupplierOrigin] =
    useState(false)
  const [autoSelectedBatchOK, setAutoSelectedBatchOK] = useState(false)

  const [allowedWarehouseIds, setAllowedWarehouseIds] = useState<number>(0)
  const [productOriginId, setProductOriginId] = useState<number | undefined>()

  const {
    suppliersState: { suppliers },
    getSuppliers
  } = useSuppliers()

  const {
    suppliersOriginState: { suppliersOrigin },
    getSuppliersOrigin
  } = useSuppliersOrigin()

  const requestAsnReturn = (
    preInboundId: number,
    page?: number,
    pageSize?: number
  ) => {
    callApi(
      AsnReturnOrderDetailReturnBatches(preInboundId, page, pageSize)
    ).catch((error) => {
      console.error(error)
    })
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestAsnReturn(selectedPreInboundId, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const tableHeaders = generateTableHeaders(selectedBatches)

  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)

    requestAsnReturn(selectedPreInboundId, 0, pagination.pageSize)
  }, [selectedFilters])

  useEffect(() => {
    if (!suppliers || suppliers.length === 0) {
      getSuppliers()
    }

    if (suppliersOrigin?.length === 0) {
      getSuppliersOrigin()
    }
  }, [])

  const validateSupplierForm = () => {
    return selectedSupplier <= 0 || selectedSupplierOrigin <= 0
  }

  const handleBatchSelected = (selectedBatch: SelectedBatchInfo) => {
    setSelectedBatches((prev: SelectedBatchInfo[]) => {
      // Filtrar por rowId en lugar de orderDetailId
      const filteredBatches = prev.filter(
        (batch) => batch.rowId !== selectedBatch.rowId
      )

      const newBatches = [...filteredBatches, selectedBatch]
      return newBatches
    })
  }

  const handleBatchSelectedDelete = (rowId: string) => {
    setSelectedBatches((prev) => prev.filter((item) => item.rowId !== rowId))
  }

  const convertRelatedBatchesToBatchListWithOrderId = (
    relatedBatches: RelatedBatch[]
  ) => {
    return relatedBatches.map((relatedBatch) => ({
      batchId: relatedBatch.batchId,
      batchNumber: relatedBatch.batchNumber,
      expirationDate: relatedBatch.expirationDate,
      orderDetailId: relatedBatch.orderDetailId
    }))
  }

  const convertSkuInfo = (orderItem?: OrderDetailReturnReasonsBatchInfoDto) => {
    return {
      skuId: orderItem?.skuId || 0,
      sku: orderItem?.sku || '',
      orderDetailId: orderItem?.orderDetailId || 0,
      hasBatch: true,
      hasExpirationDate: true
    }
  }

  const generateAsnDetails = (): AsnDetailsCreateDto[] => {
    if (!stateOrderDetailReturnReasonsBatch.data?.items) return []

    const asnDetails: AsnDetailsCreateDto[] = []

    stateOrderDetailReturnReasonsBatch.data.items.forEach(
      (orderDetail, index) => {
        const rowId = generateRowId(orderDetail, index)
        const selectedBatch = selectedBatches.find(
          (batch) => batch.rowId === rowId
        )

        if (selectedBatch && orderDetail.isFefoEnabled) {
          asnDetails.push({
            sku: orderDetail.sku,
            batchId: selectedBatch.batchId,
            batch: {
              batchNumber: selectedBatch.batchNumber,
              expirationDate: selectedBatch?.expirationDate
            },
            realQty: orderDetail.quantity,
            workOrderDetailId: orderDetail.workOrderDetailId,
            lpn: undefined,
            skuUnitCost: null
          })
        } else {
          asnDetails.push({
            sku: orderDetail.sku,
            batchId: null,
            batch: null,
            realQty: orderDetail.quantity,
            workOrderDetailId: orderDetail.workOrderDetailId,
            lpn: undefined,
            skuUnitCost: null
          })
        }
      }
    )

    return asnDetails
  }

  const generateOrderDetailReturnReasons =
    (): OrderDetailReturnReasonsCreateDto => {
      const returnReasonsDetails: OrderDetailReturnReasonCreateDto[] = []

      if (!stateOrderDetailReturnReasonsBatch.data?.items) {
        return {
          asnId: 0,
          returnReasonsDetails: []
        }
      }

      stateOrderDetailReturnReasonsBatch.data.items.forEach(
        (orderDetail, index) => {
          if (anyFefoEnabled) {
            const rowId = generateRowId(orderDetail, index)
            const selectedBatch = selectedBatches.find(
              (batch) => batch.rowId === rowId
            )

            if (selectedBatch) {
              returnReasonsDetails.push({
                orderDetailId: orderDetail.orderDetailId,
                returnReasonId: orderDetail.returnReasonId,
                batchId: selectedBatch.batchId,
                quantity: orderDetail.quantity
              })
            }
          } else {
            returnReasonsDetails.push({
              orderDetailId: orderDetail.orderDetailId,
              returnReasonId: orderDetail.returnReasonId,
              batchId: null,
              quantity: orderDetail.quantity
            })
          }
        }
      )

      return {
        asnId: 0,
        returnReasonsDetails
      }
    }

  const generateAsnNumber = (orderId: number, typeAsnIncomeId: number) => {
    if (typeAsnIncomeId === eTypeAsnIncome.Reverse) return `INV-${orderId}`

    if (typeAsnIncomeId === eTypeAsnIncome.UndeliverableOrder)
      return `IMP-${orderId}`

    return ''
  }

  const handleCreateAsn = () => {
    // Validación independiente
    if (validateSupplierForm()) return

    setLoading(true)
    const dataCreateAsn: AsnCreateDto = {
      asnNumber: generateAsnNumber(
        selectedAsnReturnDto?.orderId || selectedOrderId,
        selectedAsnReturnDto?.typeAsnIncomeId || selectedTypeAsnIncomeId
      ),
      asnStateId: eAsnState.Pending,
      warehouseId: selectedAsnReturnDto?.warehouseId || selectedWarehouseId,
      supplierId: selectedSupplier,
      supplierOriginId: selectedSupplierOrigin,
      typeAsnDocumentId: eTypeAsnDocument.Guía,
      typeAsnIncomeId:
        selectedAsnReturnDto?.typeAsnIncomeId || selectedTypeAsnIncomeId, // desde pre asn
      typeAsnSourceId: eTypeAsnSource.Manual,
      typeAsnprocessId: eTypeAsnProcess.Return,
      createdUserId: userId,
      courier: selectedAsnReturnDto?.courier || selectedCourier, // desde pre asn
      trackingNumber:
        selectedAsnReturnDto?.trackingNumber || selectedTrackingNumber, // desde pre asn
      asnDetails: generateAsnDetails(),
      preInboundId: selectedAsnReturnDto?.preInboundId || selectedPreInboundId, // desde pre asn
      orderDetailReturnReasons: generateOrderDetailReturnReasons()
    }

    CreateasncallApi(createAsn(dataCreateAsn))
      .then((t) => {
        setLoading(false)
        setSelectedAsnId(t.asnId)
        setSelectedAsnNumber(t.asnNumber)
        setselectedAsnStateId(t.asnStateId)
        handleStep(5)
      })
      .catch((e) => {
        setLoading(false)
        console.log(`error:`, e)
        handleSnackbar(`${e}`, false)
      })
  }

  const disableCreateButton = () => {
    if (!anyFefoEnabled) {
      return false
    }

    if (
      stateOrderDetailReturnReasonsBatch !== undefined &&
      (stateOrderDetailReturnReasonsBatch.data?.items?.length || 0) === 0
    ) {
      return true
    }

    const fefoEnabledRows =
      stateOrderDetailReturnReasonsBatch.data?.items?.filter(
        (item) => item.isFefoEnabled === true
      ) || []

    if (fefoEnabledRows.length === 0) {
      return false
    }

    // Genera rowIds para todas las filas con FEFO
    const fefoRowIds = fefoEnabledRows.map((row, idx) => {
      const originalIndex =
        stateOrderDetailReturnReasonsBatch.data?.items?.findIndex(
          (item) => item === row
        ) || idx
      return generateRowId(row, originalIndex)
    })

    // Verifica cuántas tienen lote seleccionado
    const fefoRowsWithBatch = fefoRowIds.filter((rowId) =>
      selectedBatches.some((batch) => batch.rowId === rowId)
    )

    return fefoRowsWithBatch.length !== fefoRowIds.length
  }

  const autoSelectSingleBatches = () => {
    if (!stateOrderDetailReturnReasonsBatch.data?.items) return

    const newSelectedBatches: SelectedBatchInfo[] = []

    stateOrderDetailReturnReasonsBatch.data.items.forEach(
      (orderDetail, index) => {
        if (
          orderDetail.relatedBatches &&
          orderDetail.relatedBatches.length === 1
        ) {
          const batch = orderDetail.relatedBatches[0]
          const rowId = generateRowId(orderDetail, index)

          newSelectedBatches.push({
            rowId, // Incluye el rowId único
            orderDetailId: orderDetail.orderDetailId,
            batchId: batch.batchId,
            batchNumber: batch.batchNumber,
            expirationDate: batch.expirationDate
          })
        }
      }
    )

    setSelectedBatches(newSelectedBatches)

    if (
      stateOrderDetailReturnReasonsBatch &&
      newSelectedBatches &&
      newSelectedBatches.length ===
        stateOrderDetailReturnReasonsBatch.data?.items.length
    ) {
      setAutoSelectedBatchOK(true)
    }
  }

  useEffect(() => {
    if (stateOrderDetailReturnReasonsBatch.data?.items) {
      const items = stateOrderDetailReturnReasonsBatch.data.items

      // Validar que el array no esté vacío y que TODOS los items tengan IsFefoEnabled en true
      // const allFefoEnabled =
      //   items.length > 0 && items.every((item) => item.isFefoEnabled === true)

      const anyFefoEnabled =
        items.length > 0 && items.some((item) => item.isFefoEnabled)

      // verifica que solo existe un lote. de ser así crea una seleccion automatica.
      if (anyFefoEnabled) {
        autoSelectSingleBatches()
      }
      getProductOrigin()
      setAnyFefoEnabled(anyFefoEnabled)
    } else {
      // Si no hay items, establecer en false
      setAnyFefoEnabled(false)
    }
  }, [stateOrderDetailReturnReasonsBatch.data?.items])

  // Verifica si los suppliers y supplierOrigin se actualizaron automaticamente
  // Verifica si los lotes se seleccionaron automaticamente
  // Si se cumplen estas dos condiciones se crea la Asn Automaticamente
  useEffect(() => {
    if (autoSelectedSupplierOriginOk === true) {
      // CON FEFO: cre asn automaticamente si  el proveedor, origen proveedor y seleccion de lotes (todos los skus 1 a N tengan lote asignado si es que solo tienen 1 lote) se hizo automaticamente
      if (anyFefoEnabled === true && autoSelectedBatchOK) {
        handleCreateAsn()
      }

      // SIN FEFO: Solo valida que la selección de supplier y supplier origin automatica sea 1 a 1 para los dos.
      if (anyFefoEnabled === false && autoSelectedSupplierOriginOk) {
        handleCreateAsn()
      }
    }
  }, [
    autoSelectedSupplierOriginOk,
    autoSelectedBatchOK,
    anyFefoEnabled,
    selectedBatches
  ])

  const getTableActions = (): Array<{
    key: string
    label: string
    onClick: (row: OrderDetailReturnReasonsBatchInfoDto, index?: number) => void
    icon: JSX.Element
    show?: (row: OrderDetailReturnReasonsBatchInfoDto) => boolean
  }> => {
    return [
      {
        key: 'selectedBatch',
        label: 'Asociar Lote',
        onClick: (row, index) => {
          const rowId = generateRowId(row, index || 0)
          productDunModal.openModal(
            'sm',
            { relatedBatches: row.relatedBatches, row, rowId },
            ''
          )
        },
        icon: <PlaylistAddOutlinedIcon />,
        show: (row) => row.isFefoEnabled === true
      },
      {
        key: 'deleteSelectedBatch',
        label: 'Eliminar selección Lote',
        onClick: (row, index) => {
          const rowId = generateRowId(row, index || 0)
          handleBatchSelectedDelete(rowId)
        },
        icon: <DeleteOutlineIcon />,
        show: (row) => row.isFefoEnabled === true
      },
      {
        key: 'infoBatch',
        label: 'SKU No requiere selección de lote (Sin Fefo)',
        onClick: () => {},
        icon: <InfoOutlinedIcon />,
        show: (row) => row.isFefoEnabled === false
      }
    ]
  }
  const getProductOrigin = () => {
    const poId =
      stateOrderDetailReturnReasonsBatch.data?.items?.find(
        (item) => item.productOriginId != null
      )?.productOriginId || undefined
    setProductOriginId(poId)

    // Obtenemos la bodega
    const warehouseId = getWarehouseId()
    // Obtenemos los proveedores relacionados a la bodega y la marca
    const suppliersAllowedAux =
      suppliers
        ?.filter(
          (s) =>
            s.warehouseIds.includes(warehouseId) && s.productOriginId === poId
        )
        .map((m) => m.supplierId) || []

    if (suppliersAllowedAux.length === 0)
      setSupplierError('No hay proveedor configurado, para Marca  y Bodega')

    // Si es solo uno el proveedor y el origen, se crea automaticamente
    if (suppliersAllowedAux.length === 1) {
      setSupplier(suppliersAllowedAux[0])

      const suppOriginAux =
        suppliersOrigin
          ?.filter(
            (item) => item.active && item.supplierId === suppliersAllowedAux[0]
          )
          .map((m) => m.id) || []

      // si es solo un origen, se envia a crear la ASN
      if (suppOriginAux.length === 1) {
        setSupplierOrigin(suppOriginAux[0])
        setAutoSelectedSupplierOrigin(true)
      }
    }

    return poId
  }

  const getWarehouseId = () => {
    const warehouseId = selectedAsnReturnDto?.warehouseId || selectedWarehouseId
    setAllowedWarehouseIds(warehouseId)

    return warehouseId
  }

  if (
    stateOrderDetailReturnReasonsBatch.status === Status.Loading ||
    stateCreateASN.status === Status.Loading
  ) {
    return (
      <div>
        <Typography>
          <Loading />
        </Typography>
      </div>
    )
  }

  return (
    <Fragment>
      <Paper style={{ padding: '16px', marginBottom: '16px' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant='h6'>Proveedor:</Typography>
            <SuppliersDDL
              onChange={(e: any) => setSupplier(Number(e.target.value))}
              value={selectedSupplier}
              type='outlined'
              fullWidth={true}
              allowedWarehouseIds={[allowedWarehouseIds]}
              productOriginId={productOriginId}
              error={supplierError.length > 0}
              errorText={supplierError}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant='h6'>Origen de proveedor:</Typography>
            <SupplierOriginDDL
              onChange={(e: any) => setSupplierOrigin(e.target.value as number)}
              value={selectedSupplierOrigin}
              type='outlined'
              fullWidth={true}
              supplierId={selectedSupplier}
              required={true}
            />
          </Grid>
        </Grid>
      </Paper>
      <Grid container spacing={2}>
        {anyFefoEnabled && (
          <Fragment>
            <Grid item xs={12} md={10}>
              <Typography variant='body2'>Selección de Lote:</Typography>
            </Grid>

            <Grid
              item
              xs={12}
              md={12}
              alignContent='center'
              alignItems='center'
            >
              <GenericTableV2<OrderDetailReturnReasonsBatchInfoDto>
                styling={{
                  dense: false
                }}
                columns={tableHeaders || []}
                keyField={'orderDetailId'}
                data={stateOrderDetailReturnReasonsBatch.data?.items || []}
                pagination={{
                  page: pagination.page,
                  pageSize: pagination.pageSize,
                  total:
                    stateOrderDetailReturnReasonsBatch.data?.totalItems || 0,
                  onPageChange: handlePaginationChange
                }}
                actions={getTableActions()}
              />
            </Grid>
          </Fragment>
        )}

        <Grid item md={10}></Grid>
        <Grid
          item
          xs={12}
          md={2}
          style={{ paddingTop: '17px', alignItems: 'right' }}
        >
          <Box display='flex' alignItems='center'>
            <LoadingButton
              loading={loading}
              variant='contained'
              color='primary'
              onClick={() => handleCreateAsn()}
              disabled={disableCreateButton()}
            >
              Crear Asn
            </LoadingButton>
            {loading && (
              <Typography
                variant='body2'
                style={{ whiteSpace: 'nowrap', paddingLeft: '5px' }}
              >
                Creando Asn...
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
      <Modal
        open={productDunModal.isOpen}
        onClose={productDunModal.closeModal}
        title={productDunModal.title}
        maxWidth={productDunModal.size}
      >
        <SimpleBatchSelector
          handleCloseModal={productDunModal.closeModal}
          batchList={convertRelatedBatchesToBatchListWithOrderId(
            productDunModal.extendedProps?.relatedBatches || []
          )}
          selectedSku={{
            ...convertSkuInfo(productDunModal.extendedProps?.row),
            rowId: productDunModal.extendedProps?.rowId // Pasa el rowId
          }}
          onBatchSelected={handleBatchSelected}
        />
      </Modal>
    </Fragment>
  )
}
